"use client";

import { useEffect } from "react";
import { useT } from "@/lib/i18n/client";

type Validatable = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

function isValidatable(el: EventTarget | null): el is Validatable {
  return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement;
}

// Chrome/Firefox render native form validation bubbles (required/type/
// minLength/…) in the browser's own UI language, ignoring the page's <html
// lang> entirely — so every `required` field showed English even on the
// Polish site. Overriding `validationMessage` via `setCustomValidity` from
// inside the "invalid" event, before the browser reads it to draw the
// bubble, is the standard fix (MDN's Constraint Validation API guide uses
// the same oninvalid + reset-on-input pattern, just per-field). A capturing
// listener on the document catches every field's "invalid" event without
// wiring one into each of the app's ~30 `required` inputs individually.
export function NativeValidationI18n() {
  const t = useT();

  useEffect(() => {
    function onInvalid(e: Event) {
      const el = e.target;
      if (!isValidatable(el)) return;
      const v = el.validity;
      if (v.valueMissing) {
        el.setCustomValidity(t.validation.required);
      } else if (el instanceof HTMLSelectElement) {
        el.setCustomValidity("");
      } else if (v.typeMismatch) {
        el.setCustomValidity(t.validation.typeMismatch);
      } else if (v.patternMismatch) {
        el.setCustomValidity(t.validation.patternMismatch);
      } else if (v.tooShort) {
        el.setCustomValidity(t.validation.tooShort(el.minLength));
      } else if (v.tooLong) {
        el.setCustomValidity(t.validation.tooLong(el.maxLength));
      } else if (el instanceof HTMLInputElement && v.rangeUnderflow) {
        el.setCustomValidity(t.validation.rangeUnderflow(Number(el.min)));
      } else if (el instanceof HTMLInputElement && v.rangeOverflow) {
        el.setCustomValidity(t.validation.rangeOverflow(Number(el.max)));
      } else if (v.badInput) {
        el.setCustomValidity(t.validation.badInput);
      }
    }

    // The custom message sticks until cleared, so every edit resets it —
    // otherwise a field fixed by the user would still report as invalid.
    function onInput(e: Event) {
      if (isValidatable(e.target)) e.target.setCustomValidity("");
    }

    document.addEventListener("invalid", onInvalid, true);
    document.addEventListener("input", onInput, true);
    document.addEventListener("change", onInput, true);
    return () => {
      document.removeEventListener("invalid", onInvalid, true);
      document.removeEventListener("input", onInput, true);
      document.removeEventListener("change", onInput, true);
    };
  }, [t]);

  return null;
}
